import {Page} from "@/libwyze-shared/types";
import React, {ReactElement, useEffect, useRef, useState} from "react";
import {Box, Checkbox, FormControlLabel, Typography, useTheme} from "@mui/material";
import {StyledBoxColumn, StyledBoxRow} from "@/shared/StyledComponents";
import {CheckinScreenType, CheckinState} from "./CheckinPlayerPreviewFragment";
import {CheckinLargeButton} from "@/checkin-shared/Components";
import {CheckOutlined} from "@mui/icons-material";
import {
  BORDER_RADIUS_MEDIUM, DIVIDER,
  PAGE_FRAGMENT_XSM_WIDTH,
  PD_LG,
  PD_MD,
  PD_SM,
  PD_XSM,
  SZ_XXXLG
} from "@/shared/dimens";

const CONSENT_FORM: string = `
<h3>1. Treatment</h3>
<p>I authorize Apex Dental Clinic and its clinicians to perform diagnostic procedures, including X-rays and examinations, and to administer treatment deemed necessary for my oral health.</p>

<h3>2. Records &amp; privacy</h3>
<p>My health information will be stored securely and shared only with providers involved in my care, insurance (if applicable), and as required by law. The clinic complies with the India DPDP Act, 2023 and relevant health privacy regulations.</p>

<h3>3. Financial responsibility</h3>
<p>I understand that I am responsible for all charges related to my treatment. If my insurance does not cover a service, I agree to pay the remaining balance. Payment is expected at the time of service unless other arrangements are made in advance.</p>
`;

function SignatureCanvas(props: { onSignatureChange: (hasSignature: boolean) => void }): ReactElement {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const hasDrawnRef = useRef(false);

  const getPos = (e: React.TouchEvent | React.MouseEvent) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if ("touches" in e) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }
    return {
      x: (e as React.MouseEvent).clientX - rect.left,
      y: (e as React.MouseEvent).clientY - rect.top,
    };
  };

  const startDraw = (e: React.TouchEvent | React.MouseEvent) => {
    e.preventDefault();
    drawingRef.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e: React.TouchEvent | React.MouseEvent) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#000";
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    if (!hasDrawnRef.current) {
      hasDrawnRef.current = true;
      props.onSignatureChange(true);
    }
  };

  const endDraw = () => {
    drawingRef.current = false;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    hasDrawnRef.current = false;
    props.onSignatureChange(false);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
  }, []);

  return <StyledBoxColumn style={{gap: PD_XSM, width: "100%"}}>
    <Typography variant="caption" style={{fontWeight: 600, textTransform: "uppercase", opacity: 0.5}}>
      Your signature
    </Typography>
    <Box style={{
      border: "1px solid #ccc",
      borderRadius: BORDER_RADIUS_MEDIUM,
      backgroundColor: "#fafafa",
      position: "relative",
      width: "100%",
      height: 160,
    }}>
      <canvas
        ref={canvasRef}
        style={{width: "100%", height: "100%", touchAction: "none", cursor: "crosshair"}}
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
      />
      {!hasDrawnRef.current && <Typography style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        opacity: 0.3,
        pointerEvents: "none",
      }}>
        Sign here with your finger
      </Typography>}
    </Box>
    <Typography
      style={{alignSelf: "flex-end", cursor: "pointer", opacity: 0.5, fontSize: "90%"}}
      onClick={clear}
    >
      Clear
    </Typography>
  </StyledBoxColumn>;
}

export function ConsentFormScreen(props: {
  practicePage: Page,
  pushScreenType: (st: CheckinScreenType) => void,
  checkinState: CheckinState,
  setCheckinState: (checkin: CheckinState) => void,
}): ReactElement {
  const theme = useTheme();
  const [agreed, setAgreed] = useState(false);
  const [hasSig, setHasSig] = useState(false);

  const register = props.checkinState.register;
  const signerName = [register.firstname, register.lastname].filter(Boolean).join(" ") || "Patient";
  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric"
  });

  return <StyledBoxColumn style={{width: "100%", height: "100%"}}>
    <StyledBoxRow style={{width: "100%", flex: 1, alignItems: "flex-start", gap: PD_LG, overflow: "hidden"}}>
      <StyledBoxColumn style={{flex: 1, height: "100%"}}>
        <Typography variant={"h2"} style={{fontWeight: 900}}>
          Consent &amp; privacy
        </Typography>
        <Typography style={{fontSize: "100%", opacity: 0.5}}>
          Please read and sign to confirm. This takes a moment.
        </Typography>
        <Box style={{
          width: "100%",
          overflow: "auto",
          border: DIVIDER,
          borderRadius: BORDER_RADIUS_MEDIUM,
          padding: PD_LG,
          backgroundColor: "#fff",
          height: SZ_XXXLG,
        }}>
          <div
            dangerouslySetInnerHTML={{__html: CONSENT_FORM}}
            style={{fontSize: "80%", lineHeight: 1.6}}
          />
        </Box>
        <FormControlLabel
          style={{marginLeft: PD_SM}}
          control={<Checkbox checked={agreed} onChange={(_, v) => setAgreed(v)}/>}
          label="I have read and agree to the above."
        />
      </StyledBoxColumn>
      <StyledBoxColumn style={{width: PAGE_FRAGMENT_XSM_WIDTH, flexShrink: 0, gap: PD_MD, paddingTop: 48}}>
        <StyledBoxColumn style={{
          gap: PD_XSM,
          padding: PD_MD,
          borderRadius: BORDER_RADIUS_MEDIUM,
          backgroundColor: theme.palette.grey[100],
        }}>
          <Typography variant="caption" style={{fontWeight: 600, textTransform: "uppercase", opacity: 0.5}}>
            Signing as
          </Typography>
          <Typography variant="h6" style={{fontWeight: 700}}>
            {signerName}
          </Typography>
          <Typography variant="body2" style={{opacity: 0.5}}>
            {today}
          </Typography>
        </StyledBoxColumn>
        <SignatureCanvas onSignatureChange={setHasSig}/>
      </StyledBoxColumn>
    </StyledBoxRow>
    <CheckinLargeButton
      color={"secondary"}
      startIcon={<CheckOutlined style={{fontSize: "125%"}}/>}
      disabled={!agreed || !hasSig}
      style={{alignSelf: "flex-start"}}
      onClick={() => {
        props.pushScreenType(CheckinScreenType.CHECKIN_PROGRESS);
      }}>
      Sign &amp; complete check-in
    </CheckinLargeButton>
  </StyledBoxColumn>;
}
